import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import {
  findProcedure, formatBRL, isNoExpiry, sessionsLabel, sortPackages, totalSessions, validityLabel,
} from '../packageFormat';
import styles from './PackageList.module.css';

/** Procedimentos do pacote como selos "3x Nome". */
function ProcedureTags({ pkg, procedures }) {
  const items = pkg.procedures || [];
  if (items.length === 0) return <span className={styles.none}>Nenhum procedimento</span>;
  return (
    <div className={styles.tags}>
      {items.map((proc) => {
        const label = findProcedure(procedures, proc.procedure_id)?.Label || 'Procedimento';
        return (
          <span key={proc.procedure_id} className={styles.tag} title={label}>
            <strong>{proc.quantity}x</strong>
            <span className={styles.tagLabel}>{label}</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Lista de pacotes: ativos primeiro e por nome. Tabela num invólucro com rolagem
 * horizontal, para o "Editar" da última coluna nunca sumir em tela estreita.
 */
function PackageList({ packages, procedures, canManage, deniedReason, onEdit }) {
  const activeCount = packages.filter((p) => p.active).length;
  const inactiveCount = packages.length - activeCount;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={styles.title}>Pacotes · {packages.length}</h3>
        <span className={styles.counts}>
          <span className={styles.dotActive} />{activeCount} {activeCount === 1 ? 'ativo' : 'ativos'}
          {inactiveCount > 0 && (
            <>
              <span className={styles.dotInactive} />{inactiveCount} {inactiveCount === 1 ? 'inativo' : 'inativos'}
            </>
          )}
        </span>
      </header>

      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Pacote</th>
              <th>Sessões incluídas</th>
              <th>Validade</th>
              <th className={styles.right}>Preço</th>
              <th>Status</th>
              <th className={styles.actionsHead} aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {sortPackages(packages).map((pkg) => {
              const sessions = totalSessions(pkg.procedures);
              const noExpiry = isNoExpiry(pkg.validity_days);
              return (
                <tr key={pkg.package_id} className={pkg.active ? '' : styles.muted}>
                  <td>
                    <div className={styles.identity}>
                      <span className={styles.icon}><Inventory2OutlinedIcon fontSize="small" /></span>
                      <span className={styles.identityText}>
                        <span className={styles.name}>{pkg.name}</span>
                        {pkg.description ? (
                          <span className={styles.description}>{pkg.description}</span>
                        ) : (
                          <span className={styles.description}>{sessionsLabel(sessions)}</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td><ProcedureTags pkg={pkg} procedures={procedures} /></td>
                  <td>
                    <span className={styles.validity}>
                      {noExpiry
                        ? <AllInclusiveRoundedIcon className={styles.validityIcon} />
                        : <EventAvailableOutlinedIcon className={styles.validityIcon} />}
                      {validityLabel(pkg.validity_days)}
                    </span>
                  </td>
                  <td className={styles.right}>
                    <span className={styles.price}>{formatBRL(pkg.price)}</span>
                  </td>
                  <td>
                    <span className={`${styles.status} ${pkg.active ? styles.active : styles.inactive}`}>
                      {pkg.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className={styles.actions}>
                    <Tooltip title={canManage ? 'Editar pacote' : deniedReason} arrow>
                      <span>
                        <IconButton
                          size="small"
                          className={styles.editButton}
                          onClick={() => onEdit(pkg)}
                          disabled={!canManage}
                          aria-label={`Editar ${pkg.name}`}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default PackageList;
